import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Btn, Card, Empty, EstadoBadge, Field, Input, Select, Spinner } from "./ui";
import { ProvLoc } from "./ProvLoc";
import { errMsg, fmtDate } from "./api";

const EMPTY = { nombre: "", email: "", password: "", provincia: "", localidad: "", telefono: "", rol: "colaborador" };

export const Colaboradores = ({ client, meta }) => {
  const [items, setItems] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => client.get("/colaboradores").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); }), [client]);
  useEffect(() => { load(); }, [load]);

  const update = async (u, body, msg) => {
    try {
      await client.patch(`/colaboradores/${u.id}`, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`¿Generar una contraseña temporal para ${u.nombre}?`)) return;
    try {
      const { data } = await client.post(`/colaboradores/${u.id}/reset-password`);
      window.prompt("Contraseña temporal (copiala y pasásela al colaborador):", data.password_temporal);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/colaboradores", f);
      toast.success("Colaborador creado y activo");
      setF(EMPTY);
      setShowNew(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const shown = (items || []).filter((u) => !filtro || u.estado === filtro || u.rol === filtro);
  const pendientes = (items || []).filter((u) => u.estado === "pendiente").length;

  return (
    <div className="space-y-6" data-testid="red-colaboradores">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <Select value={filtro} placeholder="Todos" options={[{ value: "pendiente", label: `Pendientes de aprobación (${pendientes})` }, { value: "activo", label: "Activos" }, { value: "suspendido", label: "Suspendidos" }, { value: "moderador", label: "Moderadores" }]} onChange={(e) => setFiltro(e.target.value)} className="!w-auto" data-testid="colab-filtro" />
        <Btn onClick={() => setShowNew((x) => !x)} data-testid="colab-nuevo"><UserPlus size={15} /> Nuevo colaborador</Btn>
      </div>
      {showNew && (
        <Card title="Crear colaborador">
          <form onSubmit={create} className="grid md:grid-cols-2 gap-4">
            <Field label="Nombre y apellido *"><Input required minLength={2} value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} data-testid="colab-nombre" /></Field>
            <Field label="Email *"><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="colab-email" /></Field>
            <Field label="Contraseña inicial * (mín. 8)"><Input required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="colab-password" /></Field>
            <Field label="Rol"><Select value={f.rol} options={[{ value: "colaborador", label: "Colaborador" }, { value: "moderador", label: "Moderador" }]} onChange={(e) => setF({ ...f, rol: e.target.value })} data-testid="colab-rol" /></Field>
            <ProvLoc meta={meta} provincia={f.provincia} localidad={f.localidad} onChange={(p) => setF({ ...f, ...p })} testid="colab" />
            <Field label="Teléfono"><Input value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} data-testid="colab-telefono" /></Field>
            <div className="md:col-span-2"><Btn type="submit" busy={busy} data-testid="colab-crear">Crear</Btn></div>
          </form>
        </Card>
      )}
      {items === null ? <Spinner /> : shown.length === 0 ? <Empty>No hay colaboradores.</Empty> : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500 font-headings">
              <tr><th className="text-left p-3">Nombre</th><th className="text-left p-3">Lugar</th><th className="p-3">Rol</th><th className="p-3">Estado</th><th className="p-3">Envíos</th><th className="p-3">Alta</th><th className="p-3 text-right">Acciones</th></tr>
            </thead>
            <tbody className="divide-y">
              {shown.map((u) => (
                <tr key={u.id} data-testid={`colab-row-${u.id}`}>
                  <td className="p-3"><div className="font-medium">{u.nombre}</div><div className="text-xs text-gray-500">{u.email}{u.telefono ? ` · ${u.telefono}` : ""}</div></td>
                  <td className="p-3 text-xs">{u.localidad}, {u.provincia}</td>
                  <td className="p-3 text-center">
                    <select value={u.rol} onChange={(e) => update(u, { rol: e.target.value }, "Rol actualizado")} className="border border-gray-300 text-xs px-2 py-1" data-testid={`colab-rol-${u.id}`}>
                      <option value="colaborador">Colaborador</option><option value="moderador">Moderador</option>
                    </select>
                  </td>
                  <td className="p-3 text-center"><EstadoBadge estado={u.estado} /></td>
                  <td className="p-3 text-center">{u.envios} <span className="text-xs text-gray-500">({u.aprobados} ok)</span></td>
                  <td className="p-3 text-center text-xs">{fmtDate(u.created_at)}</td>
                  <td className="p-3 text-right whitespace-nowrap space-x-1">
                    {u.estado !== "activo" && <Btn variant="gold" className="!px-2.5 !py-1 !text-xs" onClick={() => update(u, { estado: "activo" }, "Cuenta aprobada · se notificó por email")} data-testid={`colab-aprobar-${u.id}`}>Aprobar</Btn>}
                    {u.estado === "activo" && <Btn variant="danger" className="!px-2.5 !py-1 !text-xs" onClick={() => update(u, { estado: "suspendido" }, "Cuenta suspendida")} data-testid={`colab-suspender-${u.id}`}>Suspender</Btn>}
                    <Btn variant="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => resetPw(u)} data-testid={`colab-reset-${u.id}`}>Clave</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Colaboradores;
